/**
 * Physics Student Notes - Expanded Study Materials
 * Comprehensive notes for Years 11-13 Physics
 * Last Updated: January 31, 2026
 */

// ============================================
// IMAGE RESOURCES FOR PHYSICS NOTES
// ============================================
export const physicsImages = {
  vectors: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=800",
  pendulum: "https://images.unsplash.com/photo-1524578271613-d550eacf6090?w=800",
  circuits: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800",
  waves: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800",
  prism: "https://images.unsplash.com/photo-1518895949257-7621c3c786d7?w=800",
  thermometer: "https://images.unsplash.com/photo-1585435557343-3b092031a831?w=800",
  electricity: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800",
  magnet: "https://images.unsplash.com/photo-1565688534245-05d6b5be184a?w=800",
  forces: "https://images.unsplash.com/photo-1608178398319-48f814d0750c?w=800"
};

// ============================================
// YEAR 11 PHYSICS STUDENT NOTES
// ============================================
export const year11PhysicsStudentNotes = {
  strand1: {
    title: "Measurements",
    overview: "This strand covers the fundamental skills of measurement, understanding physical quantities, vectors, and graphical representation of data. These skills are essential for all physics investigations.",
    image: physicsImages.vectors,
    units: [
      {
        id: "1.1",
        title: "Physical Quantities and SI Units",
        image: physicsImages.thermometer,
        objectives: [
          "Express numbers in scientific notation",
          "Define the SI base units and identify their symbols",
          "Distinguish between fundamental and derived quantities",
          "Convert between units using dimensional analysis",
          "Calculate percentage errors and uncertainties"
        ],
        keyTerms: [
          { term: "Physical Quantity", definition: "A measurable property of a physical system that can be expressed as a number with a unit (e.g., mass = 5 kg)." },
          { term: "SI Units", definition: "International System of Units - the modern standardized system of measurement used in science worldwide." },
          { term: "Base Unit", definition: "A fundamental unit that cannot be broken down into simpler units. There are 7 SI base units." },
          { term: "Derived Unit", definition: "A unit made from combinations of base units (e.g., m/s for velocity, kg·m/s² for force)." },
          { term: "Scientific Notation", definition: "A way of writing very large or small numbers as a × 10ⁿ, where 1 ≤ a < 10." },
          { term: "Dimensional Analysis", definition: "A method of converting between units by treating units as algebraic quantities." },
          { term: "Percentage Error", definition: "The relative error expressed as a percentage: (|measured - true|/true) × 100%." },
          { term: "Uncertainty", definition: "The range of possible values within which the true value is expected to lie." }
        ],
        studyNotes: `
## Physical Quantities and SI Units

### What is a Physical Quantity?
A physical quantity is any property of a material or system that can be measured and expressed as a number with an appropriate unit.

**Examples:**
- Length = 5.2 meters (m)
- Time = 3.5 seconds (s)
- Mass = 12 kilograms (kg)

---

## The SI System of Units

The International System of Units (SI) provides a standardized set of units used by scientists worldwide.

### The 7 SI Base Units:

| Quantity | Unit Name | Symbol |
|----------|-----------|--------|
| Length | meter | m |
| Mass | kilogram | kg |
| Time | second | s |
| Electric Current | ampere | A |
| Temperature | kelvin | K |
| Amount of Substance | mole | mol |
| Luminous Intensity | candela | cd |

### Derived Units
Derived units are combinations of base units:
- **Velocity**: m/s (meters per second)
- **Acceleration**: m/s² (meters per second squared)
- **Force**: N = kg·m/s² (Newton)
- **Energy**: J = kg·m²/s² (Joule)
- **Power**: W = J/s (Watt)

---

## Scientific Notation

Scientific notation expresses numbers in the form: **a × 10ⁿ**

### Rules:
1. The coefficient (a) must be between 1 and 10
2. The exponent (n) shows how many places the decimal moved
3. Positive n for large numbers, negative n for small numbers

### Examples:
- 6,500,000 = 6.5 × 10⁶
- 0.00045 = 4.5 × 10⁻⁴
- 384,400 km = 3.844 × 10⁵ km

---

## Percentage Errors

When measurements differ from true values, we calculate the error:

**Percentage Error = |Measured Value - True Value| / True Value × 100%**

**Example:**
If you measure a rod as 25.3 cm when the true value is 25.0 cm:
Percentage Error = |25.3 - 25.0| / 25.0 × 100% = 1.2%
        `,
        examTips: [
          "Always include units in your answers - no unit means zero marks!",
          "When converting between SI prefixes, remember: kilo = 10³, centi = 10⁻², milli = 10⁻³",
          "In scientific notation, count decimal places carefully",
          "Show all working in calculations to get partial marks"
        ],
        practiceQuestions: [
          { question: "Convert 5.6 km to meters.", answer: "5,600 m or 5.6 × 10³ m" },
          { question: "Express 0.00067 in scientific notation.", answer: "6.7 × 10⁻⁴" },
          { question: "What is the SI unit for force?", answer: "Newton (N) = kg·m/s²" },
          { question: "Calculate the percentage error if measured value is 9.8 and true value is 10.0.", answer: "2%" }
        ]
      },
      {
        id: "1.2",
        title: "Vectors and Scalars",
        image: physicsImages.forces,
        objectives: [
          "Distinguish between vector and scalar quantities",
          "Represent vectors graphically using arrows",
          "Add and subtract vectors using graphical methods",
          "Resolve vectors into perpendicular components",
          "Calculate resultant vectors using trigonometry"
        ],
        keyTerms: [
          { term: "Scalar", definition: "A quantity that has only magnitude (size) - examples: mass, time, temperature, speed." },
          { term: "Vector", definition: "A quantity that has both magnitude and direction - examples: displacement, velocity, force." },
          { term: "Resultant", definition: "The single vector that has the same effect as two or more vectors combined." },
          { term: "Components", definition: "The parts of a vector when resolved into perpendicular directions (usually x and y)." },
          { term: "Equilibrium", definition: "When all forces on an object balance out, giving a net force of zero." }
        ],
        studyNotes: `
## Vectors and Scalars

### Scalars
Scalars are quantities with **magnitude only**:
- Mass (5 kg)
- Time (3 seconds)
- Speed (20 m/s)
- Temperature (25°C)
- Energy (100 J)

### Vectors
Vectors have both **magnitude AND direction**:
- Displacement (5 m East)
- Velocity (20 m/s North)
- Force (10 N at 30°)
- Acceleration (9.8 m/s² downward)

---

## Vector Addition

### Graphical Method (Head-to-Tail)
1. Draw the first vector to scale
2. Draw the second vector starting from the head of the first
3. The resultant goes from the start to the final point

### Parallelogram Method
1. Draw both vectors from the same point
2. Complete the parallelogram
3. The diagonal is the resultant

---

## Resolving Vectors

Any vector can be split into horizontal (x) and vertical (y) components:

**For a vector F at angle θ:**
- Horizontal component: Fₓ = F cos θ
- Vertical component: Fᵧ = F sin θ

**Finding the resultant from components:**
- Magnitude: R = √(Fₓ² + Fᵧ²)
- Direction: θ = tan⁻¹(Fᵧ/Fₓ)

---

## Example Problem

A force of 100 N acts at 37° to the horizontal.

**Find the components:**
- Fₓ = 100 × cos(37°) = 100 × 0.8 = 80 N
- Fᵧ = 100 × sin(37°) = 100 × 0.6 = 60 N

**Check:** √(80² + 60²) = √(6400 + 3600) = √10000 = 100 N ✓
        `,
        examTips: [
          "Always draw a clear diagram showing vector directions",
          "Remember: sin is for opposite, cos is for adjacent",
          "For perpendicular vectors, use Pythagoras: R = √(A² + B²)",
          "The 3-4-5 triangle is common in physics problems"
        ],
        practiceQuestions: [
          { question: "Two forces of 3N and 4N act perpendicular to each other. Find the resultant.", answer: "5N (using Pythagoras: √(3² + 4²) = 5)" },
          { question: "Is distance a vector or scalar?", answer: "Scalar - it has no direction" },
          { question: "A vector of 50N acts at 60° to horizontal. Find the horizontal component.", answer: "50 × cos(60°) = 25N" }
        ]
      },
      {
        id: "1.3",
        title: "Graphical Representation",
        objectives: [
          "Plot data accurately on graphs with appropriate scales",
          "Draw best-fit lines and curves",
          "Determine gradients and intercepts",
          "Interpret physical meaning from graph shapes",
          "Linearize non-linear relationships"
        ],
        keyTerms: [
          { term: "Gradient", definition: "The slope of a line, calculated as rise/run or Δy/Δx. Often has physical meaning." },
          { term: "Intercept", definition: "The point where a line crosses an axis. Y-intercept is the value when x = 0." },
          { term: "Best-fit Line", definition: "A straight line drawn through plotted points that best represents the data trend." },
          { term: "Linearization", definition: "Transforming a curved relationship into a straight line by changing variables." }
        ],
        studyNotes: `
## Graphical Analysis in Physics

### Why Use Graphs?
- Visualize relationships between variables
- Find values not directly measured (interpolation/extrapolation)
- Determine physical quantities from gradient or intercept
- Identify the type of relationship between variables

---

## Common Physics Graphs

### 1. Distance-Time Graphs
- **Gradient = Speed**
- Horizontal line = stationary
- Steeper slope = faster motion

### 2. Velocity-Time Graphs
- **Gradient = Acceleration**
- **Area under curve = Distance traveled**
- Horizontal line = constant velocity

### 3. Force-Extension Graphs (Springs)
- **Gradient = Spring constant (k)**
- Linear region shows Hooke's Law

---

## Calculating Gradient

**Gradient = Δy / Δx = (y₂ - y₁) / (x₂ - x₁)**

**Steps:**
1. Choose two points far apart on the line
2. Find the change in y (rise)
3. Find the change in x (run)
4. Include units in your answer

---

## Linearization

Some relationships appear curved but can be made linear:

| Original Relationship | How to Linearize |
|----------------------|------------------|
| y = kx² | Plot y vs x² |
| y = k/x | Plot y vs 1/x |
| y = k√x | Plot y vs √x |
        `,
        examTips: [
          "Use more than half the graph paper when plotting",
          "Choose convenient scales (avoid awkward numbers like 3)",
          "For gradient, use large triangle covering most of the line",
          "Always include units with gradient and intercept values"
        ],
        practiceQuestions: [
          { question: "What does the gradient of a velocity-time graph represent?", answer: "Acceleration" },
          { question: "What does the area under a velocity-time graph represent?", answer: "Distance traveled" },
          { question: "How would you linearize y = 3x²?", answer: "Plot y against x² - gradient will be 3" }
        ]
      }
    ]
  },
  strand2: {
    title: "Mechanics",
    overview: "Mechanics is the study of motion and forces. This strand covers kinematics (describing motion), dynamics (causes of motion), and energy transformations.",
    image: physicsImages.pendulum,
    units: [
      {
        id: "2.1",
        title: "Kinematics - Motion in a Straight Line",
        image: physicsImages.forces,
        objectives: [
          "Define displacement, velocity, and acceleration",
          "Distinguish between speed and velocity",
          "Use kinematic equations for uniformly accelerated motion",
          "Interpret displacement-time and velocity-time graphs",
          "Describe motion due to gravity (free fall)"
        ],
        keyTerms: [
          { term: "Displacement", definition: "The change in position of an object, measured as a straight line from start to end. It's a vector (has direction)." },
          { term: "Velocity", definition: "The rate of change of displacement. velocity = displacement/time. It's a vector." },
          { term: "Speed", definition: "The rate of change of distance. speed = distance/time. It's a scalar (no direction)." },
          { term: "Acceleration", definition: "The rate of change of velocity. a = Δv/Δt. Measured in m/s²." },
          { term: "Uniform Motion", definition: "Motion with constant velocity (zero acceleration)." },
          { term: "Free Fall", definition: "Motion under the influence of gravity only, with acceleration g ≈ 10 m/s² downward." }
        ],
        studyNotes: `
## Kinematics - Describing Motion

### Key Quantities

| Quantity | Symbol | Unit | Vector? |
|----------|--------|------|---------|
| Displacement | s | m | Yes |
| Velocity | v | m/s | Yes |
| Speed | - | m/s | No |
| Acceleration | a | m/s² | Yes |
| Time | t | s | No |

---

## The Kinematic Equations (SUVAT)

For motion with **constant acceleration**:

1. **v = u + at**
2. **s = ut + ½at²**
3. **s = ½(u + v)t**
4. **v² = u² + 2as**

**Where:**
- s = displacement
- u = initial velocity
- v = final velocity
- a = acceleration
- t = time

---

## Free Fall

Objects falling freely near Earth's surface accelerate at:
**g = 9.8 m/s² ≈ 10 m/s²** (downward)

**Key Points:**
- All objects fall at the same rate (ignoring air resistance)
- Velocity increases by ~10 m/s every second
- At the highest point, velocity = 0 (but acceleration = g)

**Example:**
A ball dropped from rest. After 3 seconds:
- v = u + at = 0 + 10 × 3 = 30 m/s
- s = ut + ½at² = 0 + ½ × 10 × 9 = 45 m
        `,
        examTips: [
          "Always list the known values (u, v, a, s, t) before choosing an equation",
          "Watch for hidden information: 'starts from rest' means u = 0",
          "Remember: acceleration due to gravity is positive downward",
          "For objects thrown upward, use g = -10 m/s² if taking up as positive"
        ],
        practiceQuestions: [
          { question: "A car accelerates from 5 m/s to 25 m/s in 4 seconds. Find the acceleration.", answer: "a = (25-5)/4 = 5 m/s²" },
          { question: "A ball is dropped from a tower. How far does it fall in 2 seconds? (g = 10 m/s²)", answer: "s = ½ × 10 × 4 = 20 m" },
          { question: "What is the velocity of a ball 3 seconds after being dropped?", answer: "v = 0 + 10 × 3 = 30 m/s" }
        ]
      },
      {
        id: "2.2",
        title: "Forces and Newton's Laws",
        image: physicsImages.forces,
        objectives: [
          "Define force and state its SI unit",
          "State and apply Newton's three laws of motion",
          "Identify different types of forces",
          "Draw and interpret free-body diagrams",
          "Calculate net force and resultant acceleration"
        ],
        keyTerms: [
          { term: "Force", definition: "A push or pull that can change an object's motion. Measured in Newtons (N)." },
          { term: "Newton (N)", definition: "The SI unit of force. 1 N = 1 kg·m/s² (the force needed to accelerate 1 kg at 1 m/s²)." },
          { term: "Net Force", definition: "The vector sum of all forces acting on an object. Also called resultant force." },
          { term: "Weight", definition: "The gravitational force on an object. W = mg (mass × gravitational field strength)." },
          { term: "Normal Force", definition: "The perpendicular contact force from a surface, preventing objects from passing through." },
          { term: "Friction", definition: "A force that opposes motion between surfaces in contact." },
          { term: "Tension", definition: "The pulling force transmitted through a string, rope, or cable." }
        ],
        studyNotes: `
## Newton's Laws of Motion

### Newton's First Law (Law of Inertia)
*An object at rest stays at rest, and an object in motion stays in motion at constant velocity, unless acted upon by a net force.*

**Key Points:**
- Objects resist changes in their motion
- A moving object continues forever if no forces act
- Forces cause acceleration, not motion itself

---

### Newton's Second Law
*The acceleration of an object is directly proportional to the net force and inversely proportional to its mass.*

**F = ma** or **a = F/m**

**Where:**
- F = net force (N)
- m = mass (kg)
- a = acceleration (m/s²)

---

### Newton's Third Law
*For every action, there is an equal and opposite reaction.*

**Examples:**
- You push the wall, the wall pushes you back
- Rocket pushes gas down, gas pushes rocket up
- Earth pulls you down, you pull Earth up

**Important:** Action-reaction pairs act on DIFFERENT objects!

---

## Free-Body Diagrams

A free-body diagram shows all forces acting on a single object.

**Steps to draw:**
1. Isolate the object (draw it as a dot or box)
2. Draw all forces as arrows starting from the center
3. Label each force with its name and value
4. Forces should be to scale if possible

**Common forces to include:**
- Weight (W = mg) - always points down
- Normal force (N) - perpendicular to surface
- Friction (f) - parallel to surface, opposes motion
- Tension (T) - along the string/rope
- Applied force (F) - in direction of push/pull
        `,
        examTips: [
          "In F = ma, F must be the NET force, not just one force",
          "Weight and mass are different: Weight = mass × g",
          "Action-reaction pairs are on DIFFERENT objects",
          "Draw free-body diagrams carefully - show direction and relative size"
        ],
        practiceQuestions: [
          { question: "A 5 kg mass is accelerated at 2 m/s². What is the net force?", answer: "F = ma = 5 × 2 = 10 N" },
          { question: "What is the weight of a 60 kg person? (g = 10 m/s²)", answer: "W = mg = 60 × 10 = 600 N" },
          { question: "A 10 N force and a 4 N force act in opposite directions on an object. What is the net force?", answer: "Net force = 10 - 4 = 6 N (in direction of larger force)" }
        ]
      },
      {
        id: "2.3",
        title: "Work, Energy and Power",
        objectives: [
          "Define work and calculate work done by a force",
          "Define kinetic energy and potential energy",
          "Apply the work-energy theorem",
          "State and apply the law of conservation of energy",
          "Define power and calculate power output"
        ],
        keyTerms: [
          { term: "Work", definition: "Energy transferred when a force moves an object. W = Fd cos θ. Measured in Joules (J)." },
          { term: "Energy", definition: "The capacity to do work. Measured in Joules (J)." },
          { term: "Kinetic Energy", definition: "Energy of motion. KE = ½mv²" },
          { term: "Potential Energy", definition: "Stored energy due to position or condition. Gravitational PE = mgh" },
          { term: "Conservation of Energy", definition: "Energy cannot be created or destroyed, only transferred between forms." },
          { term: "Power", definition: "The rate of doing work or transferring energy. P = W/t = Fv. Measured in Watts (W)." },
          { term: "Efficiency", definition: "The ratio of useful output energy to total input energy, expressed as percentage." }
        ],
        studyNotes: `
## Work and Energy

### Work Done
**W = F × d × cos θ**

**Where:**
- W = work done (Joules, J)
- F = force (Newtons, N)
- d = distance moved (meters, m)
- θ = angle between force and displacement

**Key Points:**
- Work is only done when force causes displacement
- If θ = 0° (force in direction of motion): W = Fd
- If θ = 90° (force perpendicular): W = 0
- 1 Joule = 1 Newton × 1 meter

---

## Types of Energy

### Kinetic Energy (KE)
Energy of motion: **KE = ½mv²**

### Gravitational Potential Energy (PE)
Energy due to height: **PE = mgh**

---

## Conservation of Energy

**Total energy before = Total energy after**

For a falling object:
- At the top: All PE, zero KE
- Halfway: Equal PE and KE
- At the bottom: All KE, zero PE

**mgh = ½mv²** (equating PE lost to KE gained)

---

## Power

**Power = Work / Time = Energy / Time**

**P = W/t = Fv**

**Units:** Watts (W) = Joules per second (J/s)

**Efficiency = (Useful output / Total input) × 100%**
        `,
        examTips: [
          "Work can be negative (when force opposes motion)",
          "KE depends on v² - doubling speed quadruples KE",
          "Always specify the reference point for PE calculations",
          "Power is a rate - don't forget to divide by time"
        ],
        practiceQuestions: [
          { question: "A 20 N force moves an object 5 m. Calculate the work done.", answer: "W = Fd = 20 × 5 = 100 J" },
          { question: "What is the kinetic energy of a 2 kg ball moving at 10 m/s?", answer: "KE = ½mv² = ½ × 2 × 100 = 100 J" },
          { question: "A motor does 3000 J of work in 60 seconds. What is its power?", answer: "P = W/t = 3000/60 = 50 W" }
        ]
      }
    ]
  },
  strand3: {
    title: "Electricity, Magnetism and Electromagnetism",
    overview: "This strand covers electrical circuits, Ohm's law, electrical power, and the relationship between electricity and magnetism.",
    image: physicsImages.electricity,
    units: [
      {
        id: "3.1",
        title: "Electric Circuits",
        image: physicsImages.circuits,
        objectives: [
          "Define electric current, voltage, and resistance",
          "Distinguish between conductors and insulators",
          "Draw and interpret circuit diagrams",
          "Connect ammeters and voltmeters correctly"
        ],
        keyTerms: [
          { term: "Electric Current (I)", definition: "The rate of flow of electric charge. I = Q/t. Measured in Amperes (A)." },
          { term: "Voltage (V)", definition: "The electrical potential difference that drives current flow. Also called EMF. Measured in Volts (V)." },
          { term: "Resistance (R)", definition: "Opposition to current flow. Measured in Ohms (Ω)." },
          { term: "Conductor", definition: "A material that allows current to flow easily (e.g., metals)." },
          { term: "Insulator", definition: "A material that does not allow current to flow (e.g., rubber, plastic)." },
          { term: "Ammeter", definition: "Device that measures current. Connected in SERIES with the component." },
          { term: "Voltmeter", definition: "Device that measures voltage. Connected in PARALLEL with the component." }
        ],
        studyNotes: `
## Electric Current

**Current = Charge / Time**
**I = Q / t**

**Where:**
- I = current (Amperes, A)
- Q = charge (Coulombs, C)
- t = time (seconds, s)

---

## Voltage (Potential Difference)

Voltage is the "push" that drives electrons through a circuit.

**Think of it like:**
- Water pressure in pipes
- Height difference causing water to flow

---

## Circuit Diagrams

Learn the standard symbols:
- Cell: —||—
- Battery: —||—||—
- Resistor: —□—
- Switch: —o o—
- Lamp: —⊗—
- Ammeter: —(A)—
- Voltmeter: —(V)—

---

## Ammeter and Voltmeter Rules

**Ammeter:**
- Measures current
- Connected in SERIES
- Has very LOW resistance

**Voltmeter:**
- Measures voltage
- Connected in PARALLEL
- Has very HIGH resistance
        `,
        examTips: [
          "Current is the same throughout a series circuit",
          "Voltage is the same across parallel branches",
          "Never connect an ammeter in parallel - it will damage it!",
          "Remember: Ammeter in Series, Voltmeter in Parallel"
        ],
        practiceQuestions: [
          { question: "If 10 C of charge flows in 2 seconds, what is the current?", answer: "I = Q/t = 10/2 = 5 A" },
          { question: "How should an ammeter be connected?", answer: "In series with the component" },
          { question: "How should a voltmeter be connected?", answer: "In parallel with the component" }
        ]
      },
      {
        id: "3.2",
        title: "Ohm's Law and Resistance",
        objectives: [
          "State and apply Ohm's law (V = IR)",
          "Calculate total resistance in series circuits",
          "Calculate total resistance in parallel circuits",
          "Distinguish between ohmic and non-ohmic conductors"
        ],
        keyTerms: [
          { term: "Ohm's Law", definition: "The current through a conductor is directly proportional to voltage, if temperature is constant: V = IR" },
          { term: "Ohmic Conductor", definition: "A component that obeys Ohm's law - has constant resistance (e.g., metal wire at constant temperature)." },
          { term: "Non-Ohmic", definition: "A component whose resistance changes with current or voltage (e.g., filament lamp, diode)." }
        ],
        studyNotes: `
## Ohm's Law

**V = I × R**

**Where:**
- V = voltage (Volts, V)
- I = current (Amperes, A)
- R = resistance (Ohms, Ω)

**Rearranged forms:**
- I = V / R
- R = V / I

---

## Series Circuits

Components connected one after another (same current flows through all).

**Rules:**
- Current is the SAME through all components
- Voltage DIVIDES between components
- Total resistance: **R_total = R₁ + R₂ + R₃ + ...**

---

## Parallel Circuits

Components connected side by side (current has multiple paths).

**Rules:**
- Voltage is the SAME across all branches
- Current DIVIDES between branches
- Total resistance: **1/R_total = 1/R₁ + 1/R₂ + 1/R₃ + ...**

**Note:** Total resistance in parallel is LESS than the smallest individual resistance!

---

## Example Calculations

**Series: Two resistors of 4Ω and 6Ω**
R_total = 4 + 6 = 10Ω

**Parallel: Two resistors of 4Ω and 6Ω**
1/R_total = 1/4 + 1/6 = 3/12 + 2/12 = 5/12
R_total = 12/5 = 2.4Ω
        `,
        examTips: [
          "Use the triangle method: V at top, I and R at bottom",
          "In series, add resistances directly",
          "In parallel, add reciprocals then flip",
          "Parallel resistance is always less than the smallest resistor"
        ],
        practiceQuestions: [
          { question: "A current of 2A flows through a 5Ω resistor. What is the voltage?", answer: "V = IR = 2 × 5 = 10V" },
          { question: "Two 6Ω resistors are in series. What is the total resistance?", answer: "R = 6 + 6 = 12Ω" },
          { question: "Two 6Ω resistors are in parallel. What is the total resistance?", answer: "1/R = 1/6 + 1/6 = 2/6; R = 3Ω" }
        ]
      },
      {
        id: "3.3",
        title: "Electrical Power and Energy",
        objectives: [
          "Define electrical power and energy",
          "Apply P = IV, P = I²R, P = V²/R",
          "Calculate energy consumption in kWh",
          "Calculate efficiency of electrical devices"
        ],
        keyTerms: [
          { term: "Electrical Power", definition: "The rate at which electrical energy is transferred. P = IV. Measured in Watts (W)." },
          { term: "Kilowatt-hour (kWh)", definition: "A unit of energy = 1000W × 1 hour = 3,600,000 J. Used for electricity billing." }
        ],
        studyNotes: `
## Electrical Power

**P = I × V = I²R = V²/R**

**Where:**
- P = power (Watts, W)
- I = current (A)
- V = voltage (V)
- R = resistance (Ω)

---

## Electrical Energy

**E = P × t = I × V × t**

**Units:**
- Joules (J) for scientific calculations
- Kilowatt-hours (kWh) for electricity bills

**Converting:**
1 kWh = 1000W × 3600s = 3,600,000 J

---

## Electricity Bills

**Cost = Power (kW) × Time (hours) × Price per kWh**

**Example:**
A 2 kW heater runs for 3 hours at 20 vatu/kWh:
- Energy = 2 × 3 = 6 kWh
- Cost = 6 × 20 = 120 vatu
        `,
        examTips: [
          "Choose the power formula based on given information",
          "Convert to kW and hours for energy calculations",
          "Remember: P = IV for any circuit component"
        ],
        practiceQuestions: [
          { question: "What is the power of a device drawing 5A at 240V?", answer: "P = IV = 5 × 240 = 1200W = 1.2kW" },
          { question: "A 100W bulb runs for 10 hours. Energy used in kWh?", answer: "E = 0.1kW × 10h = 1 kWh" }
        ]
      }
    ]
  },
  strand4: {
    title: "Light and Waves",
    overview: "This strand covers the properties of light, reflection, refraction, and wave phenomena.",
    image: physicsImages.prism,
    units: [
      {
        id: "4.1",
        title: "Light and Reflection",
        image: physicsImages.prism,
        objectives: [
          "Describe the nature of light",
          "State and apply the laws of reflection",
          "Draw ray diagrams for plane mirrors",
          "Locate images in plane mirrors"
        ],
        keyTerms: [
          { term: "Light", definition: "A form of electromagnetic radiation that is visible to the human eye. Travels at 3 × 10⁸ m/s in a vacuum." },
          { term: "Ray", definition: "A line showing the direction in which light travels." },
          { term: "Normal", definition: "A line perpendicular to a surface at the point where light strikes it." },
          { term: "Angle of Incidence", definition: "The angle between the incident ray and the normal." },
          { term: "Angle of Reflection", definition: "The angle between the reflected ray and the normal." },
          { term: "Laws of Reflection", definition: "1) Angle of incidence = Angle of reflection. 2) Incident ray, reflected ray, and normal lie in the same plane." },
          { term: "Virtual Image", definition: "An image that cannot be projected onto a screen - rays appear to come from it but don't actually pass through it." }
        ],
        studyNotes: `
## Light

### Properties of Light
- Travels in straight lines (rectilinear propagation)
- Speed in vacuum: c = 3 × 10⁸ m/s
- Can be reflected, refracted, and absorbed
- Part of the electromagnetic spectrum

---

## Laws of Reflection

**First Law:** The angle of incidence equals the angle of reflection
**i = r**

**Second Law:** The incident ray, reflected ray, and normal all lie in the same plane.

---

## Plane Mirror Images

Properties of images in a plane mirror:
- Same size as the object
- Same distance behind mirror as object is in front
- Virtual (cannot be projected)
- Laterally inverted (left-right reversed)
- Upright (not inverted top-to-bottom)
        `,
        examTips: [
          "Always measure angles from the NORMAL, not the mirror surface",
          "Remember: angle of incidence = angle of reflection",
          "Virtual images are formed where rays APPEAR to come from"
        ],
        practiceQuestions: [
          { question: "State the two laws of reflection.", answer: "1) Angle of incidence equals angle of reflection. 2) Incident ray, reflected ray, and normal lie in the same plane." },
          { question: "What type of image is formed in a plane mirror?", answer: "A virtual, upright, laterally inverted image of the same size as the object." }
        ]
      },
      {
        id: "4.2",
        title: "Refraction",
        objectives: [
          "Explain refraction of light",
          "State and apply Snell's law",
          "Calculate refractive index",
          "Explain total internal reflection and critical angle"
        ],
        keyTerms: [
          { term: "Refraction", definition: "The bending of light when it passes from one medium to another of different optical density." },
          { term: "Refractive Index (n)", definition: "A measure of how much light slows down in a medium. n = c/v = sin i / sin r" },
          { term: "Snell's Law", definition: "n₁ sin θ₁ = n₂ sin θ₂ - relates angles and refractive indices at a boundary." },
          { term: "Critical Angle", definition: "The angle of incidence that produces an angle of refraction of 90°." },
          { term: "Total Internal Reflection", definition: "When light is completely reflected at a boundary, occurring when the angle of incidence exceeds the critical angle." }
        ],
        studyNotes: `
## Refraction

### Why Does Light Refract?
Light changes speed when entering a different medium:
- Slower in denser medium → bends toward normal
- Faster in less dense medium → bends away from normal

---

## Snell's Law

**n₁ sin θ₁ = n₂ sin θ₂**

Or: **n = sin i / sin r** (when going from air to medium)

### Refractive Index Values:
| Medium | n |
|--------|---|
| Vacuum | 1.00 |
| Air | 1.00 |
| Water | 1.33 |
| Glass | 1.50 |
| Diamond | 2.42 |

---

## Total Internal Reflection

Occurs when:
1. Light travels from denser to less dense medium
2. Angle of incidence > critical angle

**Critical Angle:** sin c = 1/n

Applications:
- Optical fibers
- Prisms in binoculars
- Diamond sparkle
        `,
        examTips: [
          "Light bends TOWARD normal when entering denser medium",
          "Higher refractive index = slower light speed",
          "Total internal reflection only from dense to less dense medium"
        ],
        practiceQuestions: [
          { question: "What is the refractive index of water?", answer: "n = 1.33" },
          { question: "Calculate the critical angle for glass (n = 1.5).", answer: "sin c = 1/1.5 = 0.667, so c = 42°" }
        ]
      },
      {
        id: "4.3",
        title: "Wave Properties",
        objectives: [
          "Define wave terms: wavelength, frequency, amplitude, period",
          "Apply the wave equation v = fλ",
          "Distinguish between transverse and longitudinal waves"
        ],
        keyTerms: [
          { term: "Wave", definition: "A disturbance that transfers energy from one place to another without transferring matter." },
          { term: "Wavelength (λ)", definition: "The distance between two consecutive points in phase (e.g., crest to crest). Measured in meters." },
          { term: "Frequency (f)", definition: "The number of complete waves passing a point per second. Measured in Hertz (Hz)." },
          { term: "Amplitude", definition: "The maximum displacement from the equilibrium position. Related to energy." },
          { term: "Period (T)", definition: "The time for one complete wave cycle. T = 1/f" },
          { term: "Transverse Wave", definition: "A wave where oscillation is perpendicular to direction of travel (e.g., light, water waves)." },
          { term: "Longitudinal Wave", definition: "A wave where oscillation is parallel to direction of travel (e.g., sound)." }
        ],
        studyNotes: `
## The Wave Equation

**v = f × λ**

Where:
- v = wave speed (m/s)
- f = frequency (Hz)
- λ = wavelength (m)

Also: **f = 1/T** and **v = λ/T**

---

## Wave Types

### Transverse Waves
- Oscillation ⊥ to direction of travel
- Examples: light, water surface waves, electromagnetic waves
- Can be polarized

### Longitudinal Waves
- Oscillation ∥ to direction of travel
- Examples: sound, pressure waves
- Cannot be polarized

---

## Wave Properties Summary

| Property | Symbol | Unit |
|----------|--------|------|
| Wavelength | λ | m |
| Frequency | f | Hz |
| Period | T | s |
| Amplitude | A | m |
| Speed | v | m/s |
        `,
        examTips: [
          "Wave speed = frequency × wavelength (v = fλ)",
          "Period and frequency are inverses: T = 1/f",
          "Amplitude is related to energy, not speed"
        ],
        practiceQuestions: [
          { question: "A wave has frequency 50 Hz and wavelength 2 m. What is its speed?", answer: "v = fλ = 50 × 2 = 100 m/s" },
          { question: "Is sound a transverse or longitudinal wave?", answer: "Longitudinal" }
        ]
      }
    ]
  },
  strand5: {
    title: "Thermal, Atomic and Nuclear Physics",
    overview: "This strand covers heat transfer, thermal properties of matter, atomic structure, and radioactivity.",
    image: physicsImages.thermometer,
    units: [
      {
        id: "5.1",
        title: "Heat Transfer",
        image: physicsImages.thermometer,
        objectives: [
          "Describe the three methods of heat transfer",
          "Explain conduction, convection, and radiation",
          "Apply heat transfer concepts to everyday situations"
        ],
        keyTerms: [
          { term: "Heat", definition: "Energy transferred between objects due to a temperature difference. Flows from hot to cold." },
          { term: "Temperature", definition: "A measure of the average kinetic energy of particles in a substance." },
          { term: "Conduction", definition: "Heat transfer through a material by particle collisions. Occurs in solids, liquids, and gases." },
          { term: "Convection", definition: "Heat transfer by the movement of a fluid (liquid or gas). Hot fluid rises, cold sinks." },
          { term: "Radiation", definition: "Heat transfer by electromagnetic waves. Does not require a medium - can travel through vacuum." },
          { term: "Thermal Conductor", definition: "A material that transfers heat easily (e.g., metals)." },
          { term: "Thermal Insulator", definition: "A material that does not transfer heat easily (e.g., plastic, wood, air)." }
        ],
        studyNotes: `
## Methods of Heat Transfer

### 1. Conduction
- Heat transfers through particle vibrations
- Works in solids, liquids, and gases
- Best in solids (particles close together)
- Metals are good conductors

### 2. Convection
- Heat transfers by fluid movement
- Only in liquids and gases
- Hot fluid rises (less dense)
- Cold fluid sinks (more dense)
- Creates convection currents

### 3. Radiation
- Heat transfers by electromagnetic waves
- Does not need a medium
- All objects emit radiation
- Hotter objects emit more
- Dark surfaces absorb and emit better

---

## Applications

| Application | Heat Transfer Method |
|-------------|---------------------|
| Cooking pot | Conduction (base to food) |
| Boiling water | Convection (currents) |
| Sun to Earth | Radiation |
| Sea breeze | Convection |
| Metal spoon in tea | Conduction |
        `,
        examTips: [
          "Conduction = vibrating particles, best in solids",
          "Convection = fluid movement, only in liquids and gases",
          "Radiation = electromagnetic waves, can travel through vacuum"
        ],
        practiceQuestions: [
          { question: "Explain why metals feel cold to touch.", answer: "Metals are good conductors - they conduct heat away from your warm hand quickly, making them feel cold." },
          { question: "Why does hot air rise?", answer: "Hot air is less dense than cold air, so it rises due to buoyancy (convection)." }
        ]
      },
      {
        id: "5.2",
        title: "Specific Heat Capacity",
        objectives: [
          "Define specific heat capacity",
          "Apply Q = mcΔT to solve problems",
          "Explain latent heat"
        ],
        keyTerms: [
          { term: "Specific Heat Capacity (c)", definition: "The energy required to raise the temperature of 1 kg of a substance by 1°C (or 1 K)." },
          { term: "Heat Capacity", definition: "The energy required to raise the temperature of an object by 1°C." },
          { term: "Latent Heat", definition: "Energy absorbed or released during a phase change at constant temperature." },
          { term: "Specific Latent Heat of Fusion", definition: "Energy to change 1 kg of solid to liquid at melting point." },
          { term: "Specific Latent Heat of Vaporization", definition: "Energy to change 1 kg of liquid to gas at boiling point." }
        ],
        studyNotes: `
## Specific Heat Capacity

**Q = mcΔT**

Where:
- Q = heat energy (Joules)
- m = mass (kg)
- c = specific heat capacity (J/kg°C)
- ΔT = temperature change (°C)

### Common Values:
| Substance | c (J/kg°C) |
|-----------|------------|
| Water | 4200 |
| Ice | 2100 |
| Aluminum | 900 |
| Copper | 390 |
| Iron | 450 |

Water has HIGH specific heat capacity - good for storing heat.

---

## Latent Heat

**Q = mL**

Where:
- Q = heat energy (J)
- m = mass (kg)
- L = specific latent heat (J/kg)

During phase change:
- Temperature stays constant
- Energy changes the state, not the temperature
        `,
        examTips: [
          "Remember Q = mcΔT for temperature changes",
          "Remember Q = mL for phase changes (no temperature change)",
          "Water has very high specific heat capacity (4200 J/kg°C)"
        ],
        practiceQuestions: [
          { question: "How much energy to heat 2 kg of water from 20°C to 100°C? (c = 4200 J/kg°C)", answer: "Q = mcΔT = 2 × 4200 × 80 = 672,000 J = 672 kJ" },
          { question: "Why does water take a long time to boil?", answer: "Water has a high specific heat capacity (4200 J/kg°C), so it requires a lot of energy to raise its temperature." }
        ]
      },
      {
        id: "5.3",
        title: "Atomic Structure and Radioactivity",
        objectives: [
          "Describe atomic structure",
          "Explain radioactive decay",
          "Identify types of radiation and their properties"
        ],
        keyTerms: [
          { term: "Atom", definition: "The smallest particle of an element that retains its chemical properties." },
          { term: "Nucleus", definition: "The central part of an atom containing protons and neutrons." },
          { term: "Proton", definition: "A positively charged particle in the nucleus. Number of protons = atomic number." },
          { term: "Neutron", definition: "A neutral particle in the nucleus. Mass number = protons + neutrons." },
          { term: "Electron", definition: "A negatively charged particle orbiting the nucleus." },
          { term: "Isotope", definition: "Atoms of the same element with different numbers of neutrons." },
          { term: "Radioactive Decay", definition: "The spontaneous emission of radiation from an unstable nucleus." },
          { term: "Half-life", definition: "The time taken for half of the radioactive nuclei in a sample to decay." }
        ],
        studyNotes: `
## Atomic Structure

- **Protons**: Positive charge, in nucleus
- **Neutrons**: No charge, in nucleus
- **Electrons**: Negative charge, orbit nucleus

**Atomic Number (Z)** = Number of protons
**Mass Number (A)** = Protons + Neutrons

---

## Types of Radiation

| Type | Symbol | Charge | Mass | Penetrating Power | Ionizing Power |
|------|--------|--------|------|-------------------|----------------|
| Alpha | α | +2 | 4 | Low (paper) | High |
| Beta | β | -1 | ~0 | Medium (aluminum) | Medium |
| Gamma | γ | 0 | 0 | High (lead) | Low |

---

## Half-Life

After n half-lives: **N = N₀ × (½)ⁿ**

| Half-lives | Remaining |
|------------|-----------|
| 1 | 50% |
| 2 | 25% |
| 3 | 12.5% |
| 4 | 6.25% |
        `,
        examTips: [
          "Alpha = most ionizing, least penetrating",
          "Gamma = least ionizing, most penetrating",
          "Half-life: amount halves each half-life period"
        ],
        practiceQuestions: [
          { question: "A sample has half-life 10 years. What fraction remains after 30 years?", answer: "(½)³ = 1/8 or 12.5% remains" },
          { question: "Which type of radiation is most penetrating?", answer: "Gamma (γ)" }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 12 PHYSICS STUDENT NOTES
// ============================================
export const year12PhysicsStudentNotes = {
  strand1: {
    title: "Measurements (Advanced)",
    overview: "Advanced measurement techniques including experimental design, error analysis, and data processing.",
    units: [
      {
        id: "1.1",
        title: "Experimental Design and Error Analysis",
        objectives: [
          "Design controlled experiments",
          "Identify systematic and random errors",
          "Calculate combined uncertainties",
          "Present data with appropriate significant figures"
        ],
        keyTerms: [
          { term: "Systematic Error", definition: "Consistent error in the same direction, often due to faulty equipment or technique." },
          { term: "Random Error", definition: "Unpredictable fluctuations in measurements that scatter results around the true value." },
          { term: "Precision", definition: "The consistency of repeated measurements (small random error)." },
          { term: "Accuracy", definition: "How close measurements are to the true value (small systematic error)." }
        ],
        studyNotes: `
## Error Analysis

### Types of Errors

**Systematic Errors:**
- Always in the same direction
- Cannot be reduced by repeating measurements
- Examples: zero error on a scale, parallax error

**Random Errors:**
- Scatter results in both directions
- Can be reduced by averaging multiple measurements
- Examples: timing with a stopwatch, reading a thermometer

### Reducing Errors
- Use more precise instruments
- Take multiple readings and calculate mean
- Eliminate parallax by reading at eye level
        `,
        practiceQuestions: [
          { question: "Is a zero error systematic or random?", answer: "Systematic" },
          { question: "How can random errors be reduced?", answer: "By taking multiple measurements and averaging" }
        ]
      }
    ]
  },
  strand2: {
    title: "Mechanics (Advanced)",
    overview: "Advanced mechanics including momentum, circular motion, and gravitational fields.",
    units: [
      {
        id: "2.1",
        title: "Momentum and Collisions",
        objectives: [
          "Define momentum and impulse",
          "Apply conservation of momentum",
          "Analyze elastic and inelastic collisions"
        ],
        keyTerms: [
          { term: "Momentum", definition: "The product of mass and velocity. p = mv. A vector quantity measured in kg·m/s." },
          { term: "Impulse", definition: "The change in momentum, equal to force × time. Impulse = Δp = FΔt" },
          { term: "Elastic Collision", definition: "A collision where kinetic energy is conserved (bouncy collision)." },
          { term: "Inelastic Collision", definition: "A collision where kinetic energy is not conserved (objects may stick together)." }
        ],
        studyNotes: `
## Momentum

**p = m × v**

**Conservation of Momentum:**
In any collision (no external forces):
Total momentum before = Total momentum after

**m₁u₁ + m₂u₂ = m₁v₁ + m₂v₂**

### Types of Collisions

**Elastic Collision:**
- Both momentum AND kinetic energy conserved
- Objects bounce apart

**Inelastic Collision:**
- Only momentum conserved
- Objects may stick together
- KE is lost (to heat, sound, deformation)
        `,
        practiceQuestions: [
          { question: "A 2 kg ball moving at 5 m/s. What is its momentum?", answer: "p = mv = 2 × 5 = 10 kg·m/s" },
          { question: "What is conserved in ALL collisions?", answer: "Momentum (not necessarily kinetic energy)" }
        ]
      }
    ]
  },
  strand3: {
    title: "Waves and Optics",
    overview: "Study of wave properties, sound, and light including reflection, refraction, and the electromagnetic spectrum.",
    image: physicsImages.waves,
    units: [
      {
        id: "3.1",
        title: "Wave Properties",
        objectives: [
          "Define wave terms: wavelength, frequency, amplitude, period",
          "Apply the wave equation v = fλ",
          "Distinguish between transverse and longitudinal waves"
        ],
        keyTerms: [
          { term: "Wavelength (λ)", definition: "The distance between two consecutive points in phase (e.g., crest to crest). Measured in meters." },
          { term: "Frequency (f)", definition: "The number of complete waves passing a point per second. Measured in Hertz (Hz)." },
          { term: "Amplitude", definition: "The maximum displacement from the equilibrium position. Related to energy." },
          { term: "Period (T)", definition: "The time for one complete wave cycle. T = 1/f" },
          { term: "Transverse Wave", definition: "A wave where oscillation is perpendicular to direction of travel (e.g., light, water waves)." },
          { term: "Longitudinal Wave", definition: "A wave where oscillation is parallel to direction of travel (e.g., sound)." }
        ],
        studyNotes: `
## The Wave Equation

**v = f × λ**

**Where:**
- v = wave speed (m/s)
- f = frequency (Hz)
- λ = wavelength (m)

Also: **f = 1/T** and **v = λ/T**

### Wave Types

**Transverse Waves:**
- Oscillation ⊥ to direction of travel
- Examples: light, water surface waves, electromagnetic waves
- Can be polarized

**Longitudinal Waves:**
- Oscillation ∥ to direction of travel
- Examples: sound, pressure waves
- Cannot be polarized
        `,
        practiceQuestions: [
          { question: "A wave has frequency 50 Hz and wavelength 2 m. What is its speed?", answer: "v = fλ = 50 × 2 = 100 m/s" },
          { question: "Is sound a transverse or longitudinal wave?", answer: "Longitudinal" }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 13 PHYSICS STUDENT NOTES
// ============================================
export const year13PhysicsStudentNotes = {
  strand1: {
    title: "Advanced Measurements",
    overview: "Research methodology, advanced data analysis, and uncertainty propagation.",
    units: [
      {
        id: "1.1",
        title: "Research Methods in Physics",
        objectives: [
          "Design and conduct physics investigations",
          "Analyze and evaluate experimental data",
          "Communicate findings effectively"
        ],
        keyTerms: [
          { term: "Hypothesis", definition: "A testable prediction about the relationship between variables." },
          { term: "Controlled Variable", definition: "A variable kept constant to ensure a fair test." },
          { term: "Validity", definition: "Whether an experiment measures what it claims to measure." },
          { term: "Reliability", definition: "Whether results are consistent and reproducible." }
        ],
        studyNotes: `
## The Scientific Method in Physics

1. **Observation** - Notice a phenomenon
2. **Question** - What causes this?
3. **Hypothesis** - Make a testable prediction
4. **Experiment** - Design a controlled test
5. **Analysis** - Process the data
6. **Conclusion** - Accept or reject hypothesis
7. **Communication** - Share findings
        `,
        practiceQuestions: [
          { question: "What is the purpose of a control variable?", answer: "To ensure a fair test by keeping conditions constant" }
        ]
      }
    ]
  },
  strand2: {
    title: "Fields and Forces",
    overview: "Advanced study of gravitational, electric, and magnetic fields.",
    units: [
      {
        id: "2.1",
        title: "Gravitational Fields",
        objectives: [
          "Describe gravitational field patterns",
          "Apply Newton's law of gravitation",
          "Calculate orbital mechanics"
        ],
        keyTerms: [
          { term: "Gravitational Field", definition: "A region where a mass experiences a gravitational force." },
          { term: "Field Strength (g)", definition: "The force per unit mass at a point in a gravitational field. g = F/m = GM/r²" },
          { term: "Gravitational Constant (G)", definition: "Universal constant G = 6.67 × 10⁻¹¹ N·m²/kg²" }
        ],
        studyNotes: `
## Newton's Law of Universal Gravitation

**F = G × m₁ × m₂ / r²**

**Where:**
- F = gravitational force (N)
- G = 6.67 × 10⁻¹¹ N·m²/kg²
- m₁, m₂ = masses (kg)
- r = distance between centers (m)

### Gravitational Field Strength
**g = GM/r²**

At Earth's surface: g ≈ 9.8 m/s²
        `,
        practiceQuestions: [
          { question: "Why does g decrease as you move away from Earth?", answer: "Because g = GM/r², and r increases, so g decreases (inverse square relationship)" }
        ]
      }
    ]
  },
  strand3: {
    title: "Modern Physics",
    overview: "Introduction to quantum physics, nuclear physics, and special relativity.",
    units: [
      {
        id: "3.1",
        title: "Nuclear Physics",
        objectives: [
          "Describe atomic structure",
          "Explain radioactive decay",
          "Calculate half-life problems"
        ],
        keyTerms: [
          { term: "Isotope", definition: "Atoms of the same element with different numbers of neutrons." },
          { term: "Radioactive Decay", definition: "The spontaneous emission of radiation from an unstable nucleus." },
          { term: "Half-life", definition: "The time taken for half of the radioactive nuclei in a sample to decay." },
          { term: "Alpha (α) Decay", definition: "Emission of a helium nucleus (2 protons + 2 neutrons). High ionizing, low penetrating." },
          { term: "Beta (β) Decay", definition: "Emission of an electron from the nucleus. Medium ionizing and penetrating." },
          { term: "Gamma (γ) Decay", definition: "Emission of high-energy electromagnetic radiation. Low ionizing, high penetrating." }
        ],
        studyNotes: `
## Radioactive Decay

### Types of Radiation

| Type | Symbol | Charge | Mass | Penetrating Power | Ionizing Power |
|------|--------|--------|------|-------------------|----------------|
| Alpha | α | +2 | 4 | Low (paper) | High |
| Beta | β | -1 | ~0 | Medium (aluminum) | Medium |
| Gamma | γ | 0 | 0 | High (lead) | Low |

### Half-Life Equation

After n half-lives:
**N = N₀ × (½)ⁿ**

**Where:**
- N = remaining amount
- N₀ = original amount
- n = number of half-lives
        `,
        practiceQuestions: [
          { question: "A sample has half-life 10 years. What fraction remains after 30 years?", answer: "(½)³ = 1/8 or 12.5% remains" },
          { question: "Which type of radiation is most penetrating?", answer: "Gamma (γ)" }
        ]
      }
    ]
  }
};

// Export default object with all years
export default {
  year11: year11PhysicsStudentNotes,
  year12: year12PhysicsStudentNotes,
  year13: year13PhysicsStudentNotes
};
